/** @format */

import React, { useEffect, useState } from "react";
import { Button, Form, InputGroup } from "react-bootstrap";
import { createApi } from "unsplash-js";
import { Basic } from "unsplash-js/dist/methods/photos/types";
import { ReactComponent as Camera } from "bootstrap-icons/icons/camera.svg";
import BLSpinner from "../../assets/animations/BLSpinner/BLSpinner";
import HeroImage from "./heroImage";
import { Image } from "../resources/types";
import { produce } from "immer";
import { useTranslation } from "react-i18next";
import "./imageSelector.scss";

type Props = {
  onChangeImage: (image: Image) => void;
  image: Image;
  heroImageOnly?: boolean;
};

const ImageSelector: React.FC<Props> = ({
  onChangeImage,
  image: currImage,
  heroImageOnly = false,
}) => {
  const { t: _ } = useTranslation();
  const [unsplashQuery, setUnsplashQuery] = useState<string>("");
  const [previousQuery, setPreviousQuery] = useState<string>("");
  const [loadingImages, setLoadingImages] = useState<boolean>(false);
  const [currentImageInQuery, setCurrentImageInQuery] =
    useState<boolean>(false);
  const [images, setImages] = useState<Basic[] | undefined>();

  const unsplash = createApi({
    apiUrl: `${
      process.env.REACT_APP_RESOURCES_API_URL ??
      "https://api.dev.resources.wjec.co.uk/"
    }unsplash`,
  });

  useEffect(() => {
    if (images) {
      setCurrentImageInQuery(
        images.filter((image) => image.urls.raw === currImage.src).length > 0,
      );
    }
  }, [images, currImage.src]);

  const getImages = (query: string) => {
    if (query !== "" && previousQuery !== query) {
      setLoadingImages(true);
      unsplash.search
        .getPhotos({
          perPage: 30,
          query: query,
        })
        .then((response) => {
          if (response.errors) {
            console.log("Search Error: ", response.errors[0]);
          } else {
            setImages(response.response.results);
          }
          setLoadingImages(false);
        });
    }
    setPreviousQuery(query);
  };

  const keyDown = (e: any) => {
    if (e.key === "Enter") {
      e.preventDefault();
      getImages(unsplashQuery);
    }
  };

  return (
    <>
      {currImage && (
        <HeroImage onChangeImage={onChangeImage} image={currImage} />
      )}
      {!heroImageOnly && (
        <>
          <InputGroup
            // HACK: class name used to appear invalid. Check scss for details
            className={currImage.src === "" ? "nativeValidationFix" : ""}
            hasValidation
          >
            <Form.Control
              value={unsplashQuery}
              onKeyDown={keyDown}
              onChange={(e) => setUnsplashQuery(e.currentTarget.value)}
              isInvalid={currImage.src === ""}
              placeholder="Search Unsplash"
            />
            <Button
              variant="editing"
              type="button"
              onClick={() => {
                getImages(unsplashQuery);
              }}
            >
              {_("Go")}
            </Button>
            <Form.Control.Feedback type="invalid">
              {_("Please provide a lesson image.")}
            </Form.Control.Feedback>
          </InputGroup>
          <div className="position-relative">
            <div
              className={`border d-flex text-center justify-content-center rounded p-4 mt-3 `}
              style={{
                height: "calc(160px * 2 + 4.5rem )",
                overflowY: "auto",
              }}
            >
              {loadingImages ? (
                <div className="my-auto">
                  <BLSpinner size="3rem" />
                  <br />
                </div>
              ) : images ? (
                <div
                  className="position-relative gap-4 "
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, 160px)",
                    gridTemplateRows: "auto",
                    width: "100%",
                    justifyContent: "center",
                  }}
                >
                  {images.length > 0 ? (
                    images.map((image, index: number) => (
                      <div
                        key={index}
                        className=""
                        style={{ width: "160px", height: "160px" }}
                      >
                        <Form.Check>
                          <Form.Label
                            role="button"
                            className={`position-relative ${
                              currImage.src === image.urls.raw
                                ? "border border-3 border-editing"
                                : currentImageInQuery
                                  ? "opacity-50"
                                  : ""
                            } rounded m-0`}
                            style={{
                              width: "160px",
                              height: "160px",
                              backgroundImage: `url("${image.urls.small}&fm=jpg&fit=crop&w=160&h=160")`,
                              backgroundSize: "cover",
                              backgroundPosition: "center",
                            }}
                          >
                            <Form.Check.Input
                              data-testid={`lesson-image-${index}`}
                              className="d-none"
                              name="lessonImage"
                              type="radio"
                              value={image.urls.raw}
                              checked={currImage.src === image.urls.raw}
                              onChange={(e) => {
                                onChangeImage(
                                  produce(currImage, (draft) => {
                                    draft.src = e.currentTarget.value;
                                    draft.$metadata = {
                                      ...draft.$metadata,
                                      intrinsicSize: {
                                        width: image.width,
                                        height: image.height,
                                      },
                                      alt: image.alt_description ?? "",
                                      originSrc: image.links.html,
                                      downloadSrc:
                                        image.links.download_location,
                                    };
                                  }),
                                );
                              }}
                              required
                            />
                            <small
                              className="position-absolute end-0 bottom-0 bg-black opacity-75 px-2 "
                              style={{
                                borderTopLeftRadius: "var(--bs-border-radius)",
                                borderBottomRightRadius:
                                  "var(--bs-border-radius)",
                                maxWidth: "75%",
                                maxHeight: "3rem",
                                textOverflow: "ellipsis",
                              }}
                            >
                              <a
                                className="text-white text-end"
                                rel="noreferrer"
                                href={`${image.user.links.html}?utm_source=implement&utm_medium=referral`}
                                target="_blank"
                              >
                                {image.user.name}
                              </a>
                            </small>
                          </Form.Label>
                        </Form.Check>
                      </div>
                    ))
                  ) : (
                    <p>{_("No Results")}</p>
                  )}
                </div>
              ) : (
                <p className="my-auto">
                  <Camera style={{ width: "20px", height: "20px" }} />
                  <br />
                  {_("Find images that match your lesson")}
                </p>
              )}
            </div>
            <small
              className="position-absolute rounded bg-white px-2"
              style={{ bottom: "-10px", right: "1em" }}
            >
              {_("Photos by")}{" "}
              <a
                rel="noreferrer"
                target="_blank"
                href="https://unsplash.com/?utm_source=implement&utm_medium=referral"
              >
                Unsplash
              </a>
            </small>
          </div>
        </>
      )}
    </>
  );
};

export default ImageSelector;
